import supabase from "@/services/supabaseClient";

const ACTIVITY_ROLES = new Set(["patient", "doctor", "admin", "pharmacy", "moh", "practice_team"]);
const PROFILE_WRITE_ROLES = new Set(["patient", "doctor"]);
const ADMIN_TEAM_ROLES = new Set(["super_admin", "admin", "support", "kyc_reviewer"]);

function normalizeRole(value) {
  const role = String(value || "").trim().toLowerCase();
  return ACTIVITY_ROLES.has(role) ? role : "";
}

function resolveActivityRole({ role, route, metadata }) {
  const normalized = normalizeRole(role);
  if (normalized) return normalized;

  const metadataRole = normalizeRole(metadata?.role || metadata?.primary_role);
  if (metadataRole) return metadataRole;

  const routeCandidates = [
    route,
    metadata?.pathname,
    metadata?.path,
    typeof window !== "undefined" ? window.location?.pathname : "",
  ]
    .map((candidate) => String(candidate || "").trim().toLowerCase())
    .filter(Boolean);

  if (routeCandidates.some((candidate) => /(^|\/)practice-team(?:\/|$)/.test(candidate))) {
    return "practice_team";
  }
  return "";
}

function normalizeProfileWriteRole(value) {
  const role = String(value || "").trim().toLowerCase();
  if (ADMIN_TEAM_ROLES.has(role)) return "";
  return PROFILE_WRITE_ROLES.has(role) ? role : "";
}

function resolveDisplayName(user) {
  const meta = user?.user_metadata || {};
  return String(meta?.full_name || meta?.name || meta?.display_name || "").trim();
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export async function ensureProfileFirstWrite(user) {
  const userId = String(user?.id || "").trim();
  if (!userId) return null;

  const inferredRole =
    normalizeProfileWriteRole(user?.user_metadata?.primary_role) ||
    normalizeProfileWriteRole(user?.user_metadata?.role) ||
    normalizeProfileWriteRole(user?.app_metadata?.role) ||
    normalizeProfileWriteRole(user?.role);

  const displayName = resolveDisplayName(user);
  const email = String(user?.email || "").trim() || null;
  const emailVerified = Boolean(user?.email_confirmed_at);

  try {
    const { data: existing, error: readError } = await supabase
      .from("profiles")
      .select("user_id,display_name,email,email_verified,role,role_selected")
      .eq("user_id", userId)
      .maybeSingle();

    if (readError) throw readError;

    if (!existing) {
      const insertPayload = {
        user_id: userId,
        display_name: displayName,
        email,
        email_verified: emailVerified,
        role: inferredRole || "patient",
        onboarding_completed: false,
        role_selected: false,
      };
      if (inferredRole) {
        insertPayload.role_selected = true;
      }
      const { error: insertError } = await supabase.from("profiles").insert(insertPayload);
      if (insertError) throw insertError;
      return insertPayload;
    }

    const patch = {};
    if (!String(existing?.display_name || "").trim() && displayName) patch.display_name = displayName;
    if (!String(existing?.email || "").trim() && email) patch.email = email;
    if (existing?.email_verified !== true && emailVerified) patch.email_verified = true;
    if (!String(existing?.role || "").trim() && inferredRole) patch.role = inferredRole;
    if (existing?.role_selected !== true && inferredRole) patch.role_selected = true;

    if (Object.keys(patch).length) {
      patch.updated_at = new Date().toISOString();
      const { error: updateError } = await supabase
        .from("profiles")
        .update(patch)
        .eq("user_id", userId);
      if (updateError) throw updateError;
      return { ...existing, ...patch };
    }

    return existing;
  } catch (error) {
    console.error("[user-truth] profiles first-write failed:", error?.message || error, {
      userId,
      email,
    });
    throw error;
  }
}

export async function writeOnboardingEvent({ userId, role = "", step, eventType = "step_entered", meta = {} }) {
  const normalizedUserId = String(userId || "").trim();
  const normalizedStep = String(step || "").trim();
  if (!normalizedUserId || !normalizedStep) return false;

  const payload = {
    user_id: normalizedUserId,
    event: String(eventType || "step_entered").trim(),
    step: normalizedStep,
    meta: {
      role: normalizeRole(role) || null,
      ...(meta && typeof meta === "object" ? meta : {}),
    },
  };

  let lastError = null;
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    const { error } = await supabase.from("onboarding_events").insert(payload);
    if (!error) return true;
    lastError = error;
    if (attempt < 2) {
      await wait(180);
    }
  }

  console.error("[user-truth] onboarding event write failed:", lastError?.message || lastError, payload);
  throw lastError;
}

export async function writeUserActivityEvent({
  userId,
  role = "",
  route,
  eventType = "route_visit",
  metadata = null,
}) {
  const normalizedUserId = String(userId || "").trim();
  const normalizedRoute = String(route || "").trim();
  if (!normalizedUserId || !normalizedRoute) return false;

  const resolvedRole = resolveActivityRole({ role, route: normalizedRoute, metadata });
  if (!resolvedRole) {
    console.warn("[user-truth] activity write skipped: no valid activity role", {
      eventType: String(eventType || "route_visit").trim() || "route_visit",
      route: normalizedRoute,
    });
    return false;
  }

  const payload = {
    user_id: normalizedUserId,
    role: resolvedRole,
    route: normalizedRoute,
    event_type: String(eventType || "route_visit").trim() || "route_visit",
    metadata: metadata && typeof metadata === "object" ? metadata : {},
  };

  const { error } = await supabase.from("user_activity_events").insert(payload);
  if (error) {
    console.error("[user-truth] activity write failed:", error?.message || error, payload);
    throw error;
  }
  return true;
}
